<template>
  <div class="register-container">
    <div class="register-card">
      <div class="register-header">
        <v-icon size="48" color="red-darken-1" class="mb-3">mdi-account-plus</v-icon>
        <h2 class="register-title">Înregistrare Portal HR</h2>
        <p class="register-subtitle">Creați cont de self-service angajat</p>
      </div>

      <v-stepper v-model="step" :items="['Selectare companie', 'Date cont']" flat>
        <!-- Step 1: Companie -->
        <template v-slot:item.1>
          <v-card flat>
            <v-card-text>
              <v-autocomplete
                v-model="form.companyID"
                :items="companies"
                item-title="Name"
                item-value="ID"
                label="Companie"
                prepend-inner-icon="mdi-domain"
                variant="outlined"
                density="comfortable"
                :loading="loadingCompanies"
                no-data-text="Nu există companii disponibile"
              />
            </v-card-text>
            <v-card-actions class="justify-end pa-4">
              <v-btn @click="$router.push('/login')" variant="text">Înapoi la login</v-btn>
              <v-btn color="red-darken-1" :disabled="!form.companyID" @click="step = 2">
                Continuă <v-icon class="ml-1">mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </template>

        <!-- Step 2: Date cont -->
        <template v-slot:item.2>
          <v-card flat>
            <v-card-text>
              <v-form v-model="formValid" ref="registerForm">
                <v-text-field
                  v-model="form.email"
                  label="Email de serviciu"
                  prepend-inner-icon="mdi-email"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                  :rules="[required, emailRule]"
                  type="email"
                />
                <v-text-field
                  v-model="form.internalCode"
                  label="Marcă (InternalCode)"
                  prepend-inner-icon="mdi-badge-account"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                  :rules="[required]"
                  hint="Marca angajatului din sistemul HR"
                  persistent-hint
                />
                <v-text-field
                  v-model="form.password"
                  label="Parolă"
                  prepend-inner-icon="mdi-lock"
                  :append-inner-icon="showPass ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append-inner="showPass = !showPass"
                  :type="showPass ? 'text' : 'password'"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                  :rules="[required, minLength]"
                />
                <v-text-field
                  v-model="form.confirmPassword"
                  label="Confirmare parolă"
                  prepend-inner-icon="mdi-lock-check"
                  :type="showPass ? 'text' : 'password'"
                  variant="outlined"
                  density="comfortable"
                  :rules="[required, passwordMatch]"
                />
              </v-form>

              <v-alert v-if="errorMsg" type="error" class="mt-3" variant="tonal" closable @click:close="errorMsg = ''">
                {{ errorMsg }}
              </v-alert>
            </v-card-text>
            <v-card-actions class="justify-space-between pa-4">
              <v-btn @click="step = 1" variant="text">
                <v-icon class="mr-1">mdi-arrow-left</v-icon> Înapoi
              </v-btn>
              <v-btn
                color="red-darken-1"
                :loading="loading"
                :disabled="!formValid"
                @click="register"
              >
                <v-icon class="mr-1">mdi-account-check</v-icon> Creează cont
              </v-btn>
            </v-card-actions>
          </v-card>
        </template>
      </v-stepper>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import http from "@/settings/apiSettings"
import CompanyServices from '@/services/CompanyService'

export default defineComponent({
  name: 'PortalRegister',
  data() {
    return {
      step: 1,
      formValid: false,
      loading: false,
      loadingCompanies: false,
      showPass: false,
      errorMsg: '',
      companies: [] as Array<{ ID: string; Name: string }>,
      form: {
        companyID: '',
        email: '',
        internalCode: '',
        password: '',
        confirmPassword: ''
      }
    }
  },
  async mounted() {
    this.loadingCompanies = true;
    try {
      const resp = await CompanyServices.getAll();
      this.companies = resp.data ?? [];
    } finally {
      this.loadingCompanies = false;
    }
  },
  methods: {
    required(v: any) { return !!v || 'Câmp obligatoriu' },
    emailRule(v: string) { return /.+@.+\..+/.test(v) || 'Email invalid' },
    minLength(v: string) { return v?.length >= 6 || 'Minimum 6 caractere' },
    passwordMatch(v: string) { return v === this.form.password || 'Parolele nu coincid' },

    async register() {
      this.loading = true;
      this.errorMsg = '';
      try {
        await http.client.post('/api/auth/portal-register', {
          CompanyID: this.form.companyID,
          Email: this.form.email,
          InternalCode: this.form.internalCode,
          Password: this.form.password
        });
        this.$router.push({ name: 'Login', query: { registered: '1' } });
      } catch (e: any) {
        const code = e?.response?.data?.code;
        if (code === 'MARCA_NOT_FOUND') this.errorMsg = 'Marca nu a fost găsită sau email-ul nu corespunde datelor HR.';
        else if (code === 'ACCOUNT_EXISTS') this.errorMsg = 'Există deja un cont pentru acest email.';
        else if (code === 'PORTAL_ACCOUNT_EXISTS') this.errorMsg = 'Există deja un cont portal pentru acest angajat.';
        else this.errorMsg = e?.response?.data?.message ?? 'Eroare la înregistrare. Contactați administratorul.';
      } finally {
        this.loading = false;
      }
    }
  }
})
</script>

<style scoped>
.register-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #ffe0d5 100%);
  padding: 2rem;
}
.register-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.12);
  width: 100%;
  max-width: 520px;
  overflow: hidden;
}
.register-header {
  text-align: center;
  padding: 2.5rem 2rem 1rem;
  background: linear-gradient(135deg, #f4511e 0%, #ff6f00 100%);
  color: white;
}
.register-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}
.register-subtitle {
  font-size: 0.9rem;
  opacity: 0.85;
  margin: 0;
}
</style>
